import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { ModuleProps } from '../types';
import AppHeader from '../AppHeader';
import { useLanguage } from '../LanguageContext';
import { el as elM, en as enM } from '../i18n/modules';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface OrgMember {
  id: string;
  name: string;
  role: string;
  organization: string;
  contact: string;
  team?: string;
  wbs_codes: string[];
  parent_id: string | null;
  client_contact_id: string | null;
  ai_generated?: boolean;
}

interface ClientContact {
  id: string;
  name: string;
  role: string;
  organization: string;
  contact: string;
}

interface TeamPair {
  member_id: string;
  client_contact_id: string;
  channel: string[];
  frequency: string;
  type: string;
  notes: string;
}

interface AssignedActivity {
  id: string;
  wbs_code: string;
  title: string;
  status: string;
  is_milestone: boolean;
}

const getDepthColor = (depth: number) => {
  if (depth === 0) return '#4f46e5';
  if (depth === 1) return '#16a34a';
  return '#0ea5e9';
};

const getDepthLabel = (depth: number) => {
  if (depth === 0) return 'PM / Lead';
  if (depth === 1) return 'Core team';
  return 'Extended';
};

const OrganizationChart: React.FC<ModuleProps> = ({ project, user, token, onBack, canEdit, onLogout, onProfileEdit}) => {
  const { language } = useLanguage();
  const M = language === 'en' ? enM : elM;
  const L = M.orgChart;
  const C = M.common;

  const [activeTab, setActiveTab] = useState<'team' | 'pairs'>('team');
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [clientContacts, setClientContacts] = useState<ClientContact[]>([]);
  const [pairs, setPairs] = useState<TeamPair[]>([]);
  const [stakeholders, setStakeholders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showPairModal, setShowPairModal] = useState(false);
  const [editingMember, setEditingMember] = useState<OrgMember | null>(null);
  const [selectedMemberForPair, setSelectedMemberForPair] = useState<OrgMember | null>(null);
  const [parentId, setParentId] = useState<string | null>(null);
  const [insertAboveId, setInsertAboveId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isAiGenerated, setIsAiGenerated] = useState(false);
  const [zoom, setZoom] = useState(1);
  const treeScrollRef = useRef<HTMLDivElement>(null);
  const [isPanning, setIsPanning] = useState(false);
  const panRef = useRef<{ startX: number; startY: number; startLeft: number; startTop: number } | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [assignedActivities, setAssignedActivities] = useState<AssignedActivity[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [memberForm, setMemberForm] = useState({ name: '', role: '', organization: '', contact: '', team: '' });
  const [clientForm, setClientForm] = useState({
    name: '', role: '', organization: '', contact: '',
    channel: [] as string[], frequency: 'Εβδομαδιαία', type: 'Τακτική', notes: '',
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [orgRes, stakeholdersRes] = await Promise.allSettled([
        axios.get(`${API}/modules/${project.id}/Organization%20Chart`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/modules/${project.id}/Stakeholders`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      let fetchedMembers: OrgMember[] = [];
      let fetchedContacts: ClientContact[] = [];
      let fetchedPairs: TeamPair[] = [];
      let fetchedAiGenerated = false;

      if (orgRes.status === 'fulfilled' && orgRes.value.data?.data) {
        const data = orgRes.value.data.data;
        fetchedMembers = data.members || [];
        fetchedContacts = data.client_contacts || [];
        fetchedPairs = data.pairs || [];
        fetchedAiGenerated = data.ai_generated || false;
        setIsAiGenerated(fetchedAiGenerated);
      }

      const latestStakeholders = stakeholdersRes.status === 'fulfilled'
        ? (stakeholdersRes.value.data?.data?.stakeholders || [])
        : [];
      setStakeholders(latestStakeholders);

      // Sync org members with any updates from the Stakeholder Register
      let syncNeeded = false;
      const syncedMembers = fetchedMembers.map(m => {
        const match = latestStakeholders.find((s: any) => s.name === m.name);
        if (!match) return m;
        const updated: OrgMember = {
          ...m,
          role: match.role ?? m.role,
          organization: match.organization ?? m.organization,
          contact: match.contact ?? m.contact,
          team: match.team !== undefined ? match.team : m.team,
        };
        if (updated.role !== m.role || updated.organization !== m.organization ||
            updated.contact !== m.contact || updated.team !== m.team) {
          syncNeeded = true;
        }
        return updated;
      });

      setMembers(syncedMembers);
      setClientContacts(fetchedContacts);
      setPairs(fetchedPairs);

      if (syncNeeded) {
        await axios.put(
          `${API}/modules/${project.id}/Organization%20Chart`,
          { data: { members: syncedMembers, client_contacts: fetchedContacts, pairs: fetchedPairs, ai_generated: fetchedAiGenerated }, userId: user.id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (err) {
      console.error('Error fetching org chart:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestStakeholders = async (): Promise<any[]> => {
    try {
      const res = await axios.get(`${API}/modules/${project.id}/Stakeholders`, { headers: { Authorization: `Bearer ${token}` } });
      const latest = res.data?.data?.stakeholders || [];
      setStakeholders(latest);
      return latest;
    } catch { return stakeholders; }
  };

  const fetchAssignedActivities = async (memberName: string) => {
    setLoadingActivities(true);
    try {
      const response = await axios.get(
        `${API}/modules/${project.id}/Schedule`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.data) {
        const data = response.data.data;
        const allItems = [...(data.activities || []), ...(data.milestones || [])];
        const assigned = allItems
          .filter((item: any) => (item.assigned_to || []).includes(memberName))
          .map((item: any) => ({
            id: item.id, wbs_code: item.wbs_code, title: item.title,
            status: item.status || 'not_started', is_milestone: item.is_milestone,
          }));
        setAssignedActivities(assigned);
      }
    } catch (err) {
      console.error('Error fetching activities:', err);
      setAssignedActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const saveData = async (updatedMembers: OrgMember[], updatedContacts: ClientContact[], updatedPairs: TeamPair[]) => {
    await axios.put(
      `${API}/modules/${project.id}/Organization%20Chart`,
      { data: { members: updatedMembers, client_contacts: updatedContacts, pairs: updatedPairs, ai_generated: isAiGenerated }, userId: user.id },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const getDepth = (memberId: string, depth = 0): number => {
    const member = members.find(m => m.id === memberId);
    if (!member || !member.parent_id) return depth;
    return getDepth(member.parent_id, depth + 1);
  };

  const toggleCollapse = (memberId: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  };

  // ── Click-and-drag panning of the org chart canvas (both axes) ──
  const handlePanStart = (e: React.MouseEvent) => {
    if (e.button !== 0 || !treeScrollRef.current) return; // left click only
    if ((e.target as HTMLElement).closest('button')) return; // let node buttons work normally
    panRef.current = {
      startX: e.clientX, startY: e.clientY,
      startLeft: treeScrollRef.current.scrollLeft, startTop: treeScrollRef.current.scrollTop,
    };
    setIsPanning(true);
  };

  const handlePanMove = useCallback((e: MouseEvent) => {
    if (!panRef.current || !treeScrollRef.current) return;
    treeScrollRef.current.scrollLeft = panRef.current.startLeft - (e.clientX - panRef.current.startX);
    treeScrollRef.current.scrollTop = panRef.current.startTop - (e.clientY - panRef.current.startY);
  }, []);

  const handlePanEnd = useCallback(() => {
    panRef.current = null;
    setIsPanning(false);
  }, []);

  useEffect(() => {
    if (isPanning) {
      window.addEventListener('mousemove', handlePanMove);
      window.addEventListener('mouseup', handlePanEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handlePanMove);
      window.removeEventListener('mouseup', handlePanEnd);
    };
  }, [isPanning, handlePanMove, handlePanEnd]);

  const handleAddMember = async () => {
    if (!memberForm.name) return;
    setSaving(true);
    try {
      const newMember: OrgMember = {
        id: editingMember?.id || `member_${Date.now()}`,
        name: memberForm.name, role: memberForm.role,
        organization: memberForm.organization, contact: memberForm.contact,
        team: memberForm.team || undefined,
        wbs_codes: editingMember?.wbs_codes || [],
        parent_id: editingMember ? editingMember.parent_id : parentId,
        client_contact_id: editingMember?.client_contact_id || null,
      };
      let updatedMembers = editingMember
        ? members.map(m => m.id === editingMember.id ? newMember : m)
        : [...members, newMember];
      // Re-parent the previous root when inserting above it
      if (!editingMember && insertAboveId) {
        updatedMembers = updatedMembers.map(m =>
          m.id === insertAboveId ? { ...m, parent_id: newMember.id } : m,
        );
      }
      await saveData(updatedMembers, clientContacts, pairs);
      setMembers(updatedMembers);
      resetMemberForm();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleDeleteMember = async (memberId: string) => {
    const updatedMembers = members.filter(m => m.id !== memberId && m.parent_id !== memberId);
    const updatedPairs = pairs.filter(p => p.member_id !== memberId);
    await saveData(updatedMembers, clientContacts, updatedPairs);
    setMembers(updatedMembers);
    setPairs(updatedPairs);
  };

  const handleAddPair = async () => {
    if (!selectedMemberForPair || !clientForm.name) return;
    setSaving(true);
    try {
      const newContact: ClientContact = {
        id: `contact_${Date.now()}`, name: clientForm.name, role: clientForm.role,
        organization: clientForm.organization, contact: clientForm.contact,
      };
      const newPair: TeamPair = {
        member_id: selectedMemberForPair.id, client_contact_id: newContact.id,
        channel: clientForm.channel, frequency: clientForm.frequency,
        type: clientForm.type, notes: clientForm.notes,
      };
      const updatedContacts = [...clientContacts, newContact];
      const updatedPairs = [...pairs, newPair];
      await saveData(members, updatedContacts, updatedPairs);
      setClientContacts(updatedContacts);
      setPairs(updatedPairs);
      resetPairForm();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleDeletePair = async (memberId: string, contactId: string) => {
    const updatedPairs = pairs.filter(p => !(p.member_id === memberId && p.client_contact_id === contactId));
    const usedContactIds = updatedPairs.map(p => p.client_contact_id);
    const updatedContacts = clientContacts.filter(c => usedContactIds.includes(c.id));
    await saveData(members, updatedContacts, updatedPairs);
    setPairs(updatedPairs);
    setClientContacts(updatedContacts);
  };

  const resetMemberForm = () => {
    setMemberForm({ name: '', role: '', organization: '', contact: '', team: '' });
    setEditingMember(null); setParentId(null); setInsertAboveId(null);
    setAssignedActivities([]); setShowMemberModal(false);
  };

  const resetPairForm = () => {
    setClientForm({ name: '', role: '', organization: '', contact: '', channel: [], frequency: 'Εβδομαδιαία', type: 'Τακτική', notes: '' });
    setSelectedMemberForPair(null); setShowPairModal(false);
  };

  const toggleChannel = (ch: string) => {
    setClientForm(prev => ({
      ...prev,
      channel: prev.channel.includes(ch) ? prev.channel.filter(c => c !== ch) : [...prev.channel, ch],
    }));
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return L.statusCompleted;
      case 'in_progress': return L.statusInProgress;
      case 'delayed': return L.statusDelayed;
      default: return L.statusNotStarted;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return { bg: '#dcfce7', text: '#166534' };
      case 'in_progress': return { bg: '#dbeafe', text: '#1e40af' };
      case 'delayed': return { bg: '#fee2e2', text: '#991b1b' };
      default: return { bg: '#f3f4f6', text: '#6b7280' };
    }
  };

  const getSubtreeWidth = (memberId: string): number => {
    const nodeChildren = members.filter(m => m.parent_id === memberId);
    if (nodeChildren.length === 0) return 200;
    const childWidths = nodeChildren.map(c => getSubtreeWidth(c.id));
    return childWidths.reduce((a, b) => a + b, 0) + (nodeChildren.length - 1) * 24;
  };

  const BezierConnectors = ({ childMembers, childColor }: { childMembers: OrgMember[]; childColor: string }) => {
    const childWidths = childMembers.map(c => getSubtreeWidth(c.id));
    const totalWidth = childWidths.reduce((a, b) => a + b, 0) + (childMembers.length - 1) * 24;
    const parentX = totalWidth / 2;
    const svgHeight = 44;
    let x = 0;
    const childCenters = childWidths.map(w => {
      const center = x + w / 2;
      x += w + 24;
      return center;
    });
    return (
      <svg width={totalWidth} height={svgHeight} style={{ display: 'block', margin: '0 auto', overflow: 'visible' }}>
        {childMembers.map((_, i) => {
          const childX = childCenters[i];
          const d = `M ${parentX} 0 C ${parentX} ${svgHeight * 0.65}, ${childX} ${svgHeight * 0.35}, ${childX} ${svgHeight}`;
          return <path key={i} d={d} fill="none" stroke={childColor} strokeWidth="2" strokeLinecap="round" opacity="0.7" />;
        })}
      </svg>
    );
  };

  const renderNode = (member: OrgMember, depth: number) => {
    const children = members.filter(m => m.parent_id === member.id);
    const color = getDepthColor(depth);
    const isCollapsed = collapsed.has(member.id);
    const hasChildren = children.length > 0;

    return (
      <div key={member.id} style={styles.nodeWrapper}>
        <div style={{ ...styles.nodeCard, borderTop: `3px solid ${color}` }}>
          <div style={styles.nodeBody}>
            <div style={styles.nodeName}>{member.name}</div>
            <div style={{ ...styles.nodeRole, color }}>{member.role}</div>
            {member.team && <div style={styles.nodeTeam}>👥 {member.team}</div>}
            {member.organization && <div style={styles.nodeOrg}>{member.organization}</div>}
            {member.contact && <div style={{ ...styles.nodeContact, color }}>{member.contact}</div>}
          </div>
          {canEdit && (
            <div style={styles.nodeActions}>
              {hasChildren && (
                <button style={{ ...styles.nodeCollapseBtn, backgroundColor: isCollapsed ? '#ede9fe' : '#f3f4f6', color: isCollapsed ? '#4f46e5' : '#6b7280' }} onClick={() => toggleCollapse(member.id)}>
                  {isCollapsed ? '▶' : '▼'}
                </button>
              )}
              <button style={styles.nodeEditBtn} onClick={async () => {
                setEditingMember(member);
                const latest = await fetchLatestStakeholders();
                const matched = latest.find((s: any) => s.name === member.name);
                setMemberForm({
                  name: member.name,
                  role: matched ? matched.role || '' : member.role,
                  organization: matched ? matched.organization || '' : member.organization,
                  contact: matched ? matched.contact || '' : member.contact,
                  team: matched ? matched.team || '' : member.team || '',
                });
                fetchAssignedActivities(member.name);
                setShowMemberModal(true);
              }}>✎</button>
              <button style={styles.nodeDeleteBtn} onClick={() => handleDeleteMember(member.id)}>✕</button>
            </div>
          )}
          {!canEdit && hasChildren && (
            <div style={styles.nodeActions}>
              <button style={{ ...styles.nodeCollapseBtn, backgroundColor: isCollapsed ? '#ede9fe' : '#f3f4f6', color: isCollapsed ? '#4f46e5' : '#6b7280' }} onClick={() => toggleCollapse(member.id)}>
                {isCollapsed ? '▶' : '▼'}
              </button>
            </div>
          )}
        </div>
        {canEdit && (
          <button style={styles.addChildBtn} onClick={() => { setParentId(member.id); setShowMemberModal(true); }}>+</button>
        )}
        {hasChildren && !isCollapsed && (
          <div style={styles.childrenWrapper}>
            <BezierConnectors childMembers={children} childColor={getDepthColor(depth + 1)} />
            <div style={styles.childrenRow}>{children.map(child => renderNode(child, depth + 1))}</div>
          </div>
        )}
        {hasChildren && isCollapsed && <div style={styles.collapsedBadge}>{L.membersBadge(children.length)}</div>}
      </div>
    );
  };

  const roots = members.filter(m => m.parent_id === null);
  const unpaired = members.filter(m => !pairs.find(p => p.member_id === m.id));

  return (
    <div style={styles.container}>
      <AppHeader user={user} onHome={onBack} onProfileEdit={onProfileEdit} onLogout={onLogout} />

      <div style={styles.breadcrumb}>
        <span style={styles.breadcrumbLink} onClick={onBack}>{C.myProjects}</span>
        <span style={styles.breadcrumbSep}> / </span>
        <span style={styles.breadcrumbLink} onClick={onBack}>{project.name}</span>
        <span style={styles.breadcrumbSep}> / </span>
        <span style={styles.breadcrumbCurrent}>Organization Chart</span>
      </div>

      <div style={styles.main}>
        <div style={styles.titleRow}>
          <h2 style={styles.pageTitle}>Organization Chart</h2>
        </div>

        <div style={styles.tabWrapper}>
          <div style={styles.tabContainer}>
            <button style={{ ...styles.tab, ...(activeTab === 'team' ? styles.tabActive : styles.tabInactive) }} onClick={() => setActiveTab('team')}>{L.tabTeam}</button>
            <button style={{ ...styles.tab, ...(activeTab === 'pairs' ? styles.tabActive : styles.tabInactive) }} onClick={() => setActiveTab('pairs')}>Project Teams</button>
          </div>
        </div>

        {loading ? <p style={styles.loading}>{C.loading}</p> : (
          <>
            {activeTab === 'team' && (
              <div style={styles.section}>
                <div style={styles.sectionHeader}>
                  <div style={styles.sectionLeft}>
                    {isAiGenerated && <span style={styles.aiBadge}>✦ AI-generated</span>}
                    {isAiGenerated && <span style={styles.aiSubtext}>{L.aiGeneratedLabel}</span>}
                  </div>
                  <div style={styles.sectionRight}>
                    <div style={styles.zoomControls}>
                      <button style={styles.zoomBtn} onClick={() => setZoom(z => Math.max(0.3, z - 0.1))}>−</button>
                      <span style={styles.zoomLabel}>{Math.round(zoom * 100)}%</span>
                      <button style={styles.zoomBtn} onClick={() => setZoom(z => Math.min(1.5, z + 0.1))}>+</button>
                      <button style={styles.zoomResetBtn} onClick={() => setZoom(1)}>↺</button>
                      <button style={styles.zoomFitBtn} onClick={() => setZoom(0.6)}>⊡</button>
                    </div>
                    <button style={styles.collapseAllBtn} onClick={() => { const allIds = new Set(members.filter(m => members.some(c => c.parent_id === m.id)).map(m => m.id)); setCollapsed(allIds); }}>{L.btnCollapseAll}</button>
                    <button style={styles.expandAllBtn} onClick={() => setCollapsed(new Set())}>{L.btnExpandAll}</button>
                    {canEdit && (
                      <button style={styles.addBtn} onClick={() => { setParentId(null); setShowMemberModal(true); }}>{L.btnAddMember}</button>
                    )}
                  </div>
                </div>
                {members.length === 0 ? (
                  <div style={styles.emptyState}><p>{L.emptyMembers}</p></div>
                ) : (
                  <div ref={treeScrollRef} onMouseDown={handlePanStart}
                    style={{ ...styles.treeScrollWrapper, cursor: isPanning ? 'grabbing' : 'grab', userSelect: isPanning ? 'none' : undefined }}>
                    <div style={{ ...styles.treeContainer, transform: `scale(${zoom})`, transformOrigin: 'top center' }}>
                      {canEdit && (
                        <button style={{ ...styles.addChildBtn, marginBottom: '8px' }} onClick={() => { setParentId(null); setInsertAboveId(roots[0]?.id ?? null); setShowMemberModal(true); }} title={L.btnAddAbovePM}>+</button>
                      )}
                      {roots.map(root => renderNode(root, 0))}
                    </div>
                  </div>
                )}
                {members.length > 0 && (
                  <div style={styles.legend}>
                    {[0, 1, 2].map(d => (
                      <div key={d} style={styles.legendItem}>
                        <div style={{ ...styles.legendLine, backgroundColor: getDepthColor(d) }} />
                        <span style={styles.legendLabel}>{getDepthLabel(d)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'pairs' && (
              <div style={styles.section}>
                <div style={styles.sectionHeader}>
                  <h3 style={styles.sectionTitle}>{L.tabPairs}</h3>
                  {canEdit && (
                    <button style={styles.addBtn} onClick={() => setShowPairModal(true)}>{L.btnDefinePair}</button>
                  )}
                </div>
                {pairs.length > 0 && (
                  <div style={styles.pairsContainer}>
                    {pairs.map((pair, i) => {
                      const member = members.find(m => m.id === pair.member_id);
                      const contact = clientContacts.find(c => c.id === pair.client_contact_id);
                      if (!member || !contact) return null;
                      const depth = getDepth(member.id);
                      const color = getDepthColor(depth);
                      return (
                        <div key={i} style={styles.pairCard}>
                          <div style={styles.pairSide}>
                            <div style={{ ...styles.pairAvatar, backgroundColor: color }}>{member.name.charAt(0)}</div>
                            <div>
                              <div style={styles.pairName}>{member.name}</div>
                              <div style={{ ...styles.pairRole, color }}>{member.role}</div>
                              <div style={styles.pairOrg}>{member.organization}</div>
                            </div>
                          </div>
                          <div style={styles.pairConnector}>
                            <div style={styles.pairChannels}>{pair.channel.map((ch, j) => <span key={j} style={styles.channelPill}>{ch}</span>)}</div>
                            <div style={styles.pairArrow}>↔</div>
                            <div style={styles.pairMeta}>
                              <span style={styles.pairFreq}>{pair.frequency}</span>
                              <span style={styles.pairType}>{pair.type}</span>
                            </div>
                          </div>
                          <div style={styles.pairSide}>
                            <div style={{ ...styles.pairAvatar, backgroundColor: '#10b981' }}>{contact.name.charAt(0)}</div>
                            <div>
                              <div style={styles.pairName}>{contact.name}</div>
                              <div style={styles.pairRole}>{contact.role}</div>
                              <div style={styles.pairOrg}>{contact.organization}</div>
                            </div>
                          </div>
                          {canEdit && (
                            <button style={styles.pairDeleteBtn} onClick={() => handleDeletePair(pair.member_id, pair.client_contact_id)}>✕</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {unpaired.length > 0 && (
                  <div style={styles.unpairedSection}>
                    <h4 style={styles.unpairedTitle}>{L.noClientContact}</h4>
                    <div style={styles.unpairedList}>
                      {unpaired.map(member => (
                        <div key={member.id} style={styles.unpairedCard}>
                          <div style={{ ...styles.pairAvatar, backgroundColor: getDepthColor(getDepth(member.id)), width: '32px', height: '32px', fontSize: '13px' }}>{member.name.charAt(0)}</div>
                          <div style={{ flex: 1 }}>
                            <div style={styles.pairName}>{member.name}</div>
                            <div style={styles.pairRole}>{member.role}</div>
                          </div>
                          {canEdit && (
                            <button style={styles.assignBtn} onClick={() => { setSelectedMemberForPair(member); setShowPairModal(true); }}>{L.btnDefinePairSmall}</button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {pairs.length === 0 && unpaired.length === 0 && members.length === 0 && (
                  <div style={styles.emptyState}><p>{L.emptyPairs}</p></div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Member Modal */}
      {showMemberModal && canEdit && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>
              {editingMember ? L.modalEditMember : parentId ? L.modalNewSubordinate : L.modalNewMember}
            </h2>
            {parentId && !editingMember && (
              <div style={styles.parentInfo}>{L.subordinateOf} <strong>{members.find(m => m.id === parentId)?.name}</strong></div>
            )}
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldName}</label>
              {!editingMember ? (
                <>
                  <select
                    style={styles.select}
                    value={memberForm.name}
                    onChange={e => {
                      const selected = stakeholders.find((s: any) => s.name === e.target.value);
                      if (selected) setMemberForm({ name: selected.name, role: selected.role || '', organization: selected.organization || '', contact: selected.contact || '', team: selected.team || '' });
                      else setMemberForm({ name: '', role: '', organization: '', contact: '', team: '' });
                    }}
                  >
                    <option value="">{L.fieldFromStakeholder}</option>
                    {stakeholders.map((s: any) => (
                      <option key={s.id} value={s.name}>{s.name}{s.role ? ` — ${s.role}` : ''}</option>
                    ))}
                  </select>
                  {memberForm.name && (
                    <div style={{ marginTop: '6px', fontSize: '12px', color: '#4f46e5', backgroundColor: '#ede9fe', padding: '6px 10px', borderRadius: '6px' }}>
                      {L.stakeholderAutoFilled}
                    </div>
                  )}
                </>
              ) : (
                <input style={{ ...styles.input, backgroundColor: '#f9fafb', color: '#9ca3af' }} value={memberForm.name} readOnly />
              )}
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldTeam}</label>
              <input
                style={{ ...styles.input, ...(stakeholders.some(s => s.name === memberForm.name) ? { backgroundColor: '#f9fafb', color: '#9ca3af' } : {}) }}
                value={memberForm.team}
                onChange={e => setMemberForm({ ...memberForm, team: e.target.value })}
                placeholder={L.fieldTeamPlaceholder}
                readOnly={stakeholders.some(s => s.name === memberForm.name)}
              />
              {stakeholders.some(s => s.name === memberForm.name) && (
                <p style={{ fontSize: '11px', color: '#9ca3af', margin: '4px 0 0' }}>{L.fieldEditFromStakeholder}</p>
              )}
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldRole}</label>
              <input
                style={{ ...styles.input, ...(stakeholders.some(s => s.name === memberForm.name) ? { backgroundColor: '#f9fafb', color: '#9ca3af' } : {}) }}
                value={memberForm.role}
                onChange={e => setMemberForm({ ...memberForm, role: e.target.value })}
                placeholder={L.fieldRolePlaceholder}
                readOnly={stakeholders.some(s => s.name === memberForm.name)}
              />
              {stakeholders.some(s => s.name === memberForm.name) && (
                <p style={{ fontSize: '11px', color: '#9ca3af', margin: '4px 0 0' }}>{L.fieldEditFromStakeholder}</p>
              )}
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldOrganization}</label>
              <input
                style={{ ...styles.input, ...(stakeholders.some(s => s.name === memberForm.name) ? { backgroundColor: '#f9fafb', color: '#9ca3af' } : {}) }}
                value={memberForm.organization}
                onChange={e => setMemberForm({ ...memberForm, organization: e.target.value })}
                placeholder={L.fieldOrganizationPlaceholder}
                readOnly={stakeholders.some(s => s.name === memberForm.name)}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Contact (Email)</label>
              <input
                style={{ ...styles.input, ...(stakeholders.some(s => s.name === memberForm.name) ? { backgroundColor: '#f9fafb', color: '#9ca3af' } : {}) }}
                value={memberForm.contact}
                onChange={e => setMemberForm({ ...memberForm, contact: e.target.value })}
                placeholder="email@example.com"
                readOnly={stakeholders.some(s => s.name === memberForm.name)}
              />
            </div>
            {editingMember && (
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldActivities}</label>
                {loadingActivities ? (
                  <p style={styles.activitiesLoading}>{C.loading}</p>
                ) : assignedActivities.length === 0 ? (
                  <div style={styles.noActivities}>{L.activitiesEmpty}</div>
                ) : (
                  <div style={styles.activitiesList}>
                    {assignedActivities.map((activity, i) => {
                      const sc = getStatusColor(activity.status);
                      return (
                        <div key={i} style={styles.activityRow}>
                          <span style={styles.activityWbs}>{activity.wbs_code}</span>
                          <span style={styles.activityTitle}>
                            {activity.is_milestone && <span style={{ color: '#8b5cf6', marginRight: '4px' }}>◆</span>}
                            {activity.title}
                          </span>
                          <span style={{ ...styles.activityStatus, backgroundColor: sc.bg, color: sc.text }}>{getStatusLabel(activity.status)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {!editingMember && !memberForm.name && (
              <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 12px', textAlign: 'center' }}>{L.stakeholderRequired}</p>
            )}
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn} onClick={resetMemberForm}>{C.cancel}</button>
              <button style={{ ...styles.saveBtn, ...(!editingMember && !memberForm.name ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }} onClick={handleAddMember} disabled={saving || (!editingMember && !memberForm.name)}>{saving ? C.saving : C.save}</button>
            </div>
          </div>
        </div>
      )}

      {/* Pair Modal */}
      {showPairModal && canEdit && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>{L.modalPairTitle}</h2>
            <div style={styles.field}>
              <label style={styles.label}>{L.pairForMember}</label>
              <select style={styles.select} value={selectedMemberForPair?.id || ''} onChange={e => setSelectedMemberForPair(members.find(m => m.id === e.target.value) || null)}>
                <option value="">{L.pairMemberPlaceholder}</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name} — {m.role}</option>)}
              </select>
            </div>
            <div style={styles.divider} />
            <div style={styles.field}>
              <label style={styles.label}>{L.pairClientName}</label>
              <input style={styles.input} value={clientForm.name} onChange={e => setClientForm({ ...clientForm, name: e.target.value })} placeholder={L.pairClientNamePlaceholder} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.pairRole}</label>
              <input style={styles.input} value={clientForm.role} onChange={e => setClientForm({ ...clientForm, role: e.target.value })} placeholder={L.pairRolePlaceholder} />
            </div>
            <div style={styles.twoCol}>
              <div style={styles.field}>
                <label style={styles.label}>{L.pairCompany}</label>
                <input style={styles.input} value={clientForm.organization} onChange={e => setClientForm({ ...clientForm, organization: e.target.value })} placeholder={L.pairCompanyPlaceholder} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Contact</label>
                <input style={styles.input} value={clientForm.contact} onChange={e => setClientForm({ ...clientForm, contact: e.target.value })} placeholder="email@client.com" />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.pairChannels}</label>
              <div style={styles.channelPills}>
                {L.channels.map(ch => (
                  <button key={ch} style={{ ...styles.channelToggle, backgroundColor: clientForm.channel.includes(ch) ? '#4f46e5' : '#f3f4f6', color: clientForm.channel.includes(ch) ? 'white' : '#374151' }} onClick={() => toggleChannel(ch)}>{ch}</button>
                ))}
              </div>
            </div>
            <div style={styles.twoCol}>
              <div style={styles.field}>
                <label style={styles.label}>{L.pairFrequency}</label>
                <select style={styles.select} value={clientForm.frequency} onChange={e => setClientForm({ ...clientForm, frequency: e.target.value })}>
                  {L.frequencies.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>{L.pairType}</label>
                <select style={styles.select} value={clientForm.type} onChange={e => setClientForm({ ...clientForm, type: e.target.value })}>
                  {L.types.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.pairNote}</label>
              <textarea style={{ ...styles.input, height: '60px', resize: 'none' }} value={clientForm.notes} onChange={e => setClientForm({ ...clientForm, notes: e.target.value })} placeholder={L.pairNotePlaceholder} />
            </div>
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn} onClick={resetPairForm}>{C.cancel}</button>
              <button style={styles.saveBtn} onClick={handleAddPair} disabled={saving}>{saving ? C.saving : C.save}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { minHeight: '100vh', backgroundColor: '#f5f5f0' },
  header: { backgroundColor: '#1a1a2e', padding: '0 32px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { color: 'white', fontSize: '22px', fontWeight: 'bold', margin: 0 },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  userName: { color: 'white', fontSize: '14px' },
  userRole: { backgroundColor: '#4f46e5', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
  breadcrumb: { padding: '16px 32px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', fontSize: '14px', position: 'sticky' as const, top: 64, zIndex: 99 },
  breadcrumbLink: { color: '#4f46e5', cursor: 'pointer', fontWeight: '500' },
  breadcrumbSep: { color: '#9ca3af', margin: '0 8px' },
  breadcrumbCurrent: { color: '#374151', fontWeight: '500' },
  main: { padding: '32px 48px', width: '100%', boxSizing: 'border-box' },
  titleRow: { marginBottom: '20px' },
  pageTitle: { fontSize: '24px', fontWeight: '600', color: '#1a1a2e', margin: 0 },
  tabWrapper: { display: 'flex', justifyContent: 'center', marginBottom: '32px' },
  tabContainer: { display: 'inline-flex', backgroundColor: 'white', borderRadius: '999px', padding: '4px', border: '1px solid #e5e7eb', gap: '2px' },
  tab: { padding: '10px 28px', borderRadius: '999px', border: 'none', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s' },
  tabActive: { backgroundColor: '#1a1a2e', color: 'white' },
  tabInactive: { backgroundColor: 'transparent', color: '#6b7280' },
  loading: { color: '#666' },
  section: { backgroundColor: 'white', borderRadius: '16px', padding: '28px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' },
  sectionLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  sectionRight: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  sectionTitle: { fontSize: '16px', fontWeight: '600', color: '#1a1a2e', margin: 0 },
  aiBadge: { backgroundColor: '#ede9fe', color: '#4f46e5', padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '600' },
  aiSubtext: { fontSize: '13px', color: '#9ca3af' },
  zoomControls: { display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: '#f3f4f6', borderRadius: '8px', padding: '4px 8px' },
  zoomBtn: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer', fontSize: '16px', fontWeight: '600', color: '#374151' },
  zoomLabel: { fontSize: '12px', color: '#6b7280', minWidth: '36px', textAlign: 'center' },
  zoomResetBtn: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer', fontSize: '14px', color: '#6b7280' },
  zoomFitBtn: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer', fontSize: '14px', color: '#6b7280' },
  collapseAllBtn: { backgroundColor: '#f3f4f6', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', color: '#374151' },
  expandAllBtn: { backgroundColor: '#f3f4f6', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', color: '#374151' },
  addBtn: { backgroundColor: 'white', color: '#1a1a2e', border: '1px solid #e5e7eb', padding: '8px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  emptyState: { textAlign: 'center', padding: '40px', color: '#9ca3af' },
  treeScrollWrapper: { overflowX: 'auto', overflowY: 'auto', maxHeight: '65vh', padding: '16px', backgroundColor: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '12px' },
  treeContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 'max-content', transition: 'transform 0.2s' },
  nodeWrapper: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  nodeCard: { backgroundColor: 'white', borderRadius: '10px', padding: '14px 16px', width: '200px', boxShadow: '0 1px 6px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  nodeBody: { flex: 1 },
  nodeName: { fontSize: '14px', fontWeight: '600', color: '#1a1a2e', marginBottom: '2px' },
  nodeRole: { fontSize: '12px', fontWeight: '500', marginBottom: '2px' },
  nodeTeam: { fontSize: '11px', color: '#4f46e5', backgroundColor: '#ede9fe', padding: '1px 6px', borderRadius: '4px', marginBottom: '3px', display: 'inline-block' },
  nodeOrg: { fontSize: '11px', color: '#6b7280', marginBottom: '2px' },
  nodeContact: { fontSize: '11px' },
  nodeActions: { display: 'flex', flexDirection: 'column', gap: '3px', marginLeft: '8px' },
  nodeCollapseBtn: { width: '22px', height: '22px', borderRadius: '4px', border: 'none', cursor: 'pointer', fontSize: '10px' },
  nodeEditBtn: { backgroundColor: '#f3f4f6', color: '#6b7280', border: 'none', width: '22px', height: '22px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' },
  nodeDeleteBtn: { backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', width: '22px', height: '22px', borderRadius: '4px', cursor: 'pointer', fontSize: '10px' },
  addChildBtn: { backgroundColor: 'white', color: '#9ca3af', border: '1px dashed #d1d5db', width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '8px auto 0' },
  childrenWrapper: { display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', marginTop: '0' },
  verticalLine: { width: '2px', height: '24px', backgroundColor: '#e5e7eb', margin: '0 auto' },
  horizontalLine: { height: '2px', backgroundColor: '#e5e7eb', position: 'absolute', top: '24px' },
  childrenRow: { display: 'flex', gap: '24px', alignItems: 'flex-start', marginTop: '0' },
  collapsedBadge: { backgroundColor: '#f3f4f6', color: '#6b7280', fontSize: '11px', padding: '3px 10px', borderRadius: '999px', marginTop: '6px' },
  legend: { display: 'flex', gap: '24px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' },
  legendItem: { display: 'flex', alignItems: 'center', gap: '8px' },
  legendLine: { width: '24px', height: '3px', borderRadius: '2px' },
  legendLabel: { fontSize: '12px', color: '#6b7280' },
  pairsContainer: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' },
  pairCard: { display: 'flex', alignItems: 'center', gap: '16px', backgroundColor: '#f9fafb', borderRadius: '10px', padding: '16px', border: '1px solid #e5e7eb', position: 'relative' },
  pairSide: { display: 'flex', alignItems: 'center', gap: '12px', flex: 1 },
  pairAvatar: { width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '16px', fontWeight: '600', flexShrink: 0 },
  pairName: { fontSize: '14px', fontWeight: '600', color: '#1a1a2e' },
  pairRole: { fontSize: '12px', fontWeight: '500' },
  pairOrg: { fontSize: '12px', color: '#9ca3af' },
  pairConnector: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', minWidth: '120px' },
  pairChannels: { display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' },
  channelPill: { backgroundColor: '#ede9fe', color: '#4f46e5', padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: '500' },
  pairArrow: { fontSize: '20px', color: '#4f46e5', fontWeight: '600' },
  pairMeta: { display: 'flex', gap: '6px' },
  pairFreq: { backgroundColor: '#f0f9ff', color: '#0369a1', padding: '2px 8px', borderRadius: '999px', fontSize: '11px' },
  pairType: { backgroundColor: '#f0fdf4', color: '#166534', padding: '2px 8px', borderRadius: '999px', fontSize: '11px' },
  pairDeleteBtn: { position: 'absolute', top: '8px', right: '8px', backgroundColor: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '14px' },
  unpairedSection: { marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' },
  unpairedTitle: { fontSize: '14px', fontWeight: '600', color: '#6b7280', margin: '0 0 12px 0' },
  unpairedList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  unpairedCard: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', backgroundColor: '#fff7ed', borderRadius: '8px', border: '1px solid #fed7aa' },
  assignBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', flexShrink: 0 },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '32px', width: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1a1a2e', marginBottom: '20px' },
  parentInfo: { backgroundColor: '#ede9fe', padding: '8px 12px', borderRadius: '6px', fontSize: '13px', color: '#4f46e5', marginBottom: '16px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333', fontSize: '14px' },
  input: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' },
  select: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', outline: 'none', backgroundColor: 'white' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  channelPills: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  channelToggle: { padding: '6px 14px', borderRadius: '999px', border: 'none', fontSize: '13px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.15s' },
  divider: { height: '1px', backgroundColor: '#e5e7eb', margin: '16px 0' },
  modalButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  saveBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  activitiesLoading: { fontSize: '13px', color: '#9ca3af', margin: 0 },
  noActivities: { backgroundColor: '#f9fafb', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: '#9ca3af', textAlign: 'center' },
  activitiesList: { display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '200px', overflowY: 'auto' },
  activityRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' },
  activityWbs: { fontSize: '11px', fontWeight: '600', color: '#6b7280', minWidth: '40px' },
  activityTitle: { fontSize: '13px', color: '#374151', flex: 1 },
  activityStatus: { padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: '500', flexShrink: 0 },
};

export default OrganizationChart;
